import React, { useState, useMemo, ReactNode } from 'react';

import {
  OnboardingContext,
  OnboardingProfile,
  OnboardingState,
  ProfileData,
} from '.';
import { OnboardingData } from 'application-types';
import { Channel } from '../../types/ChannelType';

type Props = {
  defaultData: {
    userName?: string;
    building?: Channel;
    company?: Channel;
  };
  asyncOnComplete: (data: OnboardingData) => Promise<void>;
  asyncOnError: (error: any) => Promise<string>;
  children: ReactNode;
};

const defaultProfile = {
  fullName: undefined,
  isMarketingOptInSelected: false,
};

export const OnboardingContextProvider: React.FC<Props> = ({
  defaultData: { userName, building, company },
  asyncOnComplete,
  asyncOnError,
  children,
}) => {
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(
    OnboardingState.Idle
  );
  const [completionError, setCompletionError] = useState<string | undefined>(
    undefined
  );
  const [profile, setProfile] = useState<OnboardingProfile>({
    ...defaultProfile,
    fullName: userName,
  });
  const [selectedBuilding, setSelectedBuilding] = useState<Channel | undefined>(
    undefined
  );

  const hasProfileNameBeenUpdated = profile.fullName !== userName;

  const handleProfileChange = ({
    fullName,
    marketingOptIn,
    phone,
    location,
  }: ProfileData) => {
    setProfile({
      ...profile,
      fullName,
      isMarketingOptInSelected: marketingOptIn,
      phone,
      location,
    });
    setCompletionError(undefined);
  };

  const handleSelectedBuildingChange = (building?: Channel) => {
    setSelectedBuilding(building);
  };

  const handleOnboardingComplete = async (data: OnboardingData) => {
    setOnboardingState(OnboardingState.Loading);

    try {
      await asyncOnComplete(data);
      setOnboardingState(OnboardingState.Idle);
    } catch (err) {
      const error = await asyncOnError(err);

      setCompletionError(error);
      setOnboardingState(OnboardingState.Error);
    }
  };

  const handleBuildingOrCompanyChange = () => {
    if (completionError) {
      setCompletionError(undefined);
      setOnboardingState(OnboardingState.Idle);
    }
  };

  const contextValue = useMemo(
    () => ({
      data: {
        profile,
        building,
        company,
        selectedBuilding,
      },
      errors: {
        completionError,
      },
      onboardingState,
      hasProfileNameBeenUpdated,
      handlers: {
        updateProfile: handleProfileChange,
        completeOnboarding: handleOnboardingComplete,
        onBuildingOrCompanySelectionChange: handleBuildingOrCompanyChange,
        updateSelectedBuilding: handleSelectedBuildingChange,
      },
    }),
    [
      profile.fullName,
      profile.isMarketingOptInSelected,
      profile.phone,
      profile.location,
      building?._id,
      company?._id,
      selectedBuilding?._id,
      onboardingState,
      completionError,
      userName,
    ]
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};
